import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { useCurrentUser, useSession } from "@/lib/session";

/**
 * Authenticated user has no active OrgMembership. Two cases:
 *  1. End user typo'd platform.eon.health into their browser — they should
 *     go to the consumer app at eon.health.
 *  2. Org admin's membership was revoked / never accepted — they need to
 *     contact whoever invited them, or sales.
 */
export function NoOrgAccess() {
  const user = useCurrentUser();
  const { clear } = useSession();
  const navigate = useNavigate();

  async function signOut() {
    try {
      await api.post("/platform/auth/logout");
    } catch {
      // Best-effort.
    } finally {
      clear();
      navigate("/signin", { replace: true });
    }
  }

  return (
    <div className="w-full max-w-[460px] anim-fade-up">
      <div
        className="rounded-[var(--radius-modal)] p-8 shadow-xl-eon"
        style={{
          background: "rgba(11, 13, 18, 0.55)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(36px) saturate(180%)",
          WebkitBackdropFilter: "blur(36px) saturate(180%)",
        }}
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-slate-200)]">
          Access
        </p>
        <h1 className="mt-1 text-heading text-[var(--color-snow)] font-semibold">
          You're signed in, but you don't have platform access yet.
        </h1>
        <p className="mt-3 text-[14px] text-[var(--color-slate-200)] leading-relaxed">
          {user
            ? `Your account (${user.email}) isn't tied to any active organization on platform.eon.health. If you were invited by a teammate, ask them to resend the invite — or reach out to sales for a new org.`
            : "Your account isn't tied to any active organization on platform.eon.health."}
        </p>

        <div className="mt-6 grid gap-3">
          <a href="mailto:platform@eon.health?subject=Platform%20access" className="block">
            <Button size="lg" className="w-full">
              Email platform@eon.health
            </Button>
          </a>
          <Button
            variant="outlined"
            size="lg"
            className="w-full"
            onClick={signOut}
          >
            Sign out
          </Button>
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--color-slate-300)]">
          Looking for the consumer app? eon.health
        </p>
      </div>
    </div>
  );
}
